import { useEffect, useId, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { toast } from '@/components/ui/toast'
import { parseCurrencyInput } from '@/lib/format'
import { cn } from '@/lib/utils'
import { defaultFeeImageUrl, optionalFeeImageUrl } from '@/lib/optionalFeeIcons'
import {
  SETTINGS_IMAGE_ACCEPT,
  validateSettingsImage,
} from '@/lib/settingsImageUpload'
import { getApiErrorMessage } from '@/services/api'
import { quotesApi } from '@/services/quotes'
import closeIcon from '@/assets/icons/optional-fees/dialog/close.svg'
import cameraIcon from '@/assets/icons/optional-fees/dialog/camera.svg'
import euroIcon from '@/assets/icons/optional-fees/dialog/euro.svg'

export interface OptionalFeeDialogInitial {
  name: string
  amount: number
  is_active?: boolean
  image_file_id?: string | null
  image_url?: string | null
  icon_key?: 'bus' | 'thunder' | 'calendar' | null
  type?: string
}

export interface OptionalFeeDialogValue {
  name: string
  amount: number
  is_active: boolean
  image_file_id: string | null
  image_url: string | null
  image_removed: boolean
}

interface OptionalFeeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial?: OptionalFeeDialogInitial | null
  onSave: (value: OptionalFeeDialogValue) => void | Promise<void>
  showActive?: boolean
}

const fieldClass = cn(
  'h-10 rounded-xl bg-[#f4f5f8] px-4 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#98a2b3]',
  FORM_CONTROL_FOCUS_CLASS
)

export function OptionalFeeDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  showActive = true,
}: OptionalFeeDialogProps) {
  const { t } = useTranslation('settings')
  const id = useId()
  const [name, setName] = useState(initial?.name ?? '')
  const [amount, setAmount] = useState(initial ? String(initial.amount) : '')
  const [active, setActive] = useState(initial?.is_active ?? true)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [imageRemoved, setImageRemoved] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const existingImage = initial ? optionalFeeImageUrl(initial) : null
  const defaultImage = defaultFeeImageUrl(initial?.type ?? 'custom')
  const displayedImage =
    preview ?? (imageRemoved ? null : existingImage) ?? defaultImage
  const hasRemovableImage = Boolean(preview || (!imageRemoved && existingImage))

  useEffect(() => {
    if (!preview) return
    return () => URL.revokeObjectURL(preview)
  }, [preview])

  const pickImage = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    event.target.value = ''
    if (!selected) return

    const imageError = validateSettingsImage(selected)
    if (imageError) {
      setErrors(current => ({
        ...current,
        image: t(
          `optionalFees.validation.image${imageError === 'type' ? 'Type' : 'Size'}`
        ),
      }))
      return
    }

    setFile(selected)
    setPreview(URL.createObjectURL(selected))
    setImageRemoved(false)
    setErrors(current => ({ ...current, image: '' }))
  }

  const removeImage = () => {
    setFile(null)
    setPreview(null)
    setImageRemoved(true)
    setErrors(current => ({ ...current, image: '' }))
  }

  const save = async () => {
    const next: Record<string, string> = {}
    const parsedAmount = parseCurrencyInput(amount)
    if (!name.trim()) next.name = t('optionalFees.validation.name')
    if (parsedAmount === null) next.amount = t('optionalFees.validation.amount')
    setErrors(next)
    if (Object.keys(next).length > 0) return

    setSaving(true)
    try {
      const uploaded = file
        ? await quotesApi.uploadOptionalFeeImage(file)
        : null
      if (file && !uploaded?.data?.id)
        throw new Error(t('optionalFees.validation.image'))
      const imageFileId =
        uploaded?.data?.id ??
        (imageRemoved ? null : (initial?.image_file_id ?? null))
      await onSave({
        name: name.trim(),
        amount: parsedAmount!,
        is_active: active,
        image_file_id: imageFileId,
        image_url:
          uploaded?.data?.url ?? (imageRemoved ? null : existingImage) ?? null,
        image_removed: imageRemoved,
      })
      onOpenChange(false)
    } catch (error) {
      toast({ variant: 'destructive', title: getApiErrorMessage(error) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!saving) onOpenChange(next)
      }}
    >
      <DialogContent
        side='right'
        showCloseButton={false}
        overlayClassName='bg-black/25'
        className='tf-right-drawer-short'
      >
        <header className='tf-right-drawer-header tf-right-drawer-header-accent'>
          <DialogTitle className='min-w-0 flex-1 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
            {t(initial ? 'optionalFees.editTitle' : 'optionalFees.addTitle')}
          </DialogTitle>
          <button
            type='button'
            className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#f4f5f8]'
            aria-label={t('optionalFees.cancel')}
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            <img src={closeIcon} alt='' className='size-4' />
          </button>
        </header>

        <div className='min-h-0 flex-1 overflow-y-auto bg-[#f4f5f8] p-6'>
          <div className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_8px_-1px_rgba(38,45,66,0.12),0_1px_4px_-1px_rgba(38,45,66,0.08)]'>
            <div className='pt-4 pb-2'>
              <h3 className='border-l-4 border-[#ff5a1f] px-4 font-heading text-sm font-semibold leading-[1.2] text-[#3d434e]'>
                {t('optionalFees.information')}
              </h3>
            </div>
            <div className='flex flex-col gap-6 px-4 pb-6 pt-2'>
              <div className='space-y-1'>
                <Label
                  htmlFor={`${id}-image`}
                  className='text-xs font-medium leading-4 text-[#171a20]'
                >
                  {t('optionalFees.image')}
                </Label>
                <div className='flex items-center gap-4'>
                  <label
                    htmlFor={`${id}-image`}
                    className='flex size-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-[#e7e9ef] bg-[#f4f5f8]'
                  >
                    <img
                      src={displayedImage}
                      alt=''
                      className={
                        hasRemovableImage
                          ? 'size-full object-cover object-center'
                          : 'size-auto object-contain'
                      }
                    />
                  </label>
                  <div className='min-w-0 space-y-1'>
                    <div className='flex flex-wrap items-center gap-3'>
                      <label
                        htmlFor={`${id}-image`}
                        className='inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-dashed border-[#ff5a1f] bg-white px-3 text-sm font-medium leading-none text-[#ff5a1f]'
                      >
                        <img src={cameraIcon} alt='' className='size-4' />
                        {t('optionalFees.addPhoto')}
                      </label>
                      {hasRemovableImage && (
                        <Button
                          type='button'
                          variant='outline'
                          className='h-10 rounded-xl border-[#b91c1c] px-3 text-sm font-medium leading-none text-[#991b1b] shadow-none hover:bg-[#fef2f2] hover:text-[#991b1b]'
                          onClick={removeImage}
                        >
                          <Trash2 className='mr-2 size-4' />
                          {t('optionalFees.removeImage')}
                        </Button>
                      )}
                    </div>
                    <p className='text-xs font-medium leading-4 text-[#6c7285]'>
                      {t('optionalFees.imageHint')}
                    </p>
                  </div>
                  <input
                    id={`${id}-image`}
                    type='file'
                    accept={SETTINGS_IMAGE_ACCEPT}
                    className='sr-only'
                    onChange={pickImage}
                  />
                </div>
                {errors.image && (
                  <p className='text-sm text-destructive'>{errors.image}</p>
                )}
              </div>

              <div className='space-y-1'>
                <Label
                  htmlFor={`${id}-name`}
                  className='text-xs font-medium leading-4 text-[#171a20]'
                >
                  {t('optionalFees.name')}{' '}
                  <span className='text-[#d63b33]'>*</span>
                </Label>
                <Input
                  id={`${id}-name`}
                  value={name}
                  placeholder={t('optionalFees.namePlaceholder')}
                  maxLength={255}
                  className={fieldClass}
                  onChange={event => {
                    setName(event.target.value)
                    setErrors(current => ({ ...current, name: '' }))
                  }}
                />
                {errors.name && (
                  <p className='text-sm text-destructive'>{errors.name}</p>
                )}
              </div>

              <div className='space-y-1'>
                <Label
                  htmlFor={`${id}-amount`}
                  className='text-xs font-medium leading-4 text-[#171a20]'
                >
                  {t('optionalFees.amount')}{' '}
                  <span className='text-[#d63b33]'>*</span>
                </Label>
                <div className='relative'>
                  <img
                    src={euroIcon}
                    alt=''
                    className='pointer-events-none absolute left-4 top-1/2 z-10 size-4 -translate-y-1/2'
                  />
                  <CurrencyInput
                    id={`${id}-amount`}
                    value={amount}
                    className={`${fieldClass} pl-10`}
                    onValueChange={value => {
                      setAmount(value)
                      setErrors(current => ({ ...current, amount: '' }))
                    }}
                  />
                </div>
                {errors.amount && (
                  <p className='text-sm text-destructive'>{errors.amount}</p>
                )}
              </div>

              {showActive && (
                <div className='flex items-center justify-between'>
                  <Label
                    htmlFor={`${id}-active`}
                    className='text-sm font-medium leading-[1.4] text-[#171a20]'
                  >
                    {t('optionalFees.active')}
                  </Label>
                  <Switch
                    id={`${id}-active`}
                    checked={active}
                    onCheckedChange={setActive}
                    className='data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#8f95a6]'
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <footer className='tf-right-drawer-footer justify-end'>
          <Button
            type='button'
            variant='outline'
            className='h-10 rounded-xl border-[#e7e9ef] px-3 text-sm font-medium leading-none text-[#171a20] shadow-none'
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {t('optionalFees.cancel')}
          </Button>
          <Button
            type='button'
            className='h-10 rounded-xl bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white shadow-none hover:bg-[#e6511c]'
            onClick={() => void save()}
            disabled={saving}
          >
            {t('optionalFees.save')}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  )
}
